import { AlertTriangle, Check, Loader2, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "~/components/ui";
import type { DiagnosisStatus } from "~/features/dataImportProjects/hooks/useDataImportJobDiagnosis";
import { DipStatusBanner } from "./DipStatusBanner";

type DiagnosisResult = { results: { severity: string }[] } | null;

interface DipDiagnosisBannerProps {
  status: DiagnosisStatus;
  result: DiagnosisResult;
  onOpenDetail: () => void;
}

export function DipDiagnosisBanner({ status, result, onOpenDetail }: DipDiagnosisBannerProps) {
  const { t } = useTranslation();

  if (status === "idle") {
    return null;
  }

  if (status === "invoking" || status === "polling") {
    return (
      <DipStatusBanner
        tone="info"
        icon={<Loader2 className="size-3.5 animate-spin" />}
        title={t("feature.dataImportProjects.diagnosis.running")}
      />
    );
  }

  if (status === "failed") {
    return (
      <DipStatusBanner
        tone="error"
        icon={<X className="size-3.5" />}
        title={t("feature.dataImportProjects.diagnosis.failed")}
      />
    );
  }

  // status === "completed"
  const results = result?.results ?? [];
  let ok = 0;
  let warning = 0;
  let error = 0;
  for (const r of results) {
    if (r.severity === "ok") ok++;
    else if (r.severity === "warning") warning++;
    else if (r.severity === "error") error++;
  }
  const hasErrors = error > 0;

  return (
    <DipStatusBanner
      tone={hasErrors ? "error" : "success"}
      icon={hasErrors ? <AlertTriangle className="size-3.5" /> : <Check className="size-3.5" />}
      title={t("feature.dataImportProjects.v2.run.diagnosisBanner.complete", { ok, warning, error })}
      actions={
        <Button variant="outline" size="sm" onClick={onOpenDetail}>
          {t("ui.details")}
        </Button>
      }
    />
  );
}
